import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import { TaskStatus } from '@nexelem/graphne-api';

type CheckStatus = {
  checked: boolean;
  isIntermediate: boolean;
  isCheckable: boolean;
  isOrder: boolean;
  taskStatus?: TaskStatus;
  parentsIds?: string[];
  childrenIds?: string[];
};

type setNewItemProps = {
  taskId: string;
  isCheckable: boolean;
  isOrder: boolean;
  taskStatus?: TaskStatus;
  directParentId?: string[];
  childrenIds?: string[];
  isCheckAllFilterChecked: boolean;
};

type TreeTasksListStore = {
  treeTasksListCheckMap: Map<string, CheckStatus>;
  setNewTreeItem: (props: setNewItemProps) => void;
  addNewParent: (taskId: string, parentId: string) => void;
  onCheck: (taskId: string) => void;
  onCheckAll: () => void;
};

enableMapSet();

export const useTreeTasksListStore = create(
  immer<TreeTasksListStore>((set) => {
    return {
      treeTasksListCheckMap: new Map<string, CheckStatus>([]),
      setNewTreeItem: ({
        taskId,
        isCheckable,
        isOrder,
        taskStatus,
        directParentId,
        childrenIds,
        isCheckAllFilterChecked,
      }) => {
        set((state) => {
          const getCheckboxInitialState = () => {
            let checkState = false;
            let isIntermediateState = false;
            const isNotPlanned = taskStatus === TaskStatus.NotPlanned;

            if (isNotPlanned) {
              checkState = isCheckAllFilterChecked;
              isIntermediateState = false;
            } else if (!isNotPlanned || isOrder) {
              checkState = false;
              isIntermediateState = isCheckAllFilterChecked;
            }

            return { checked: checkState, isIntermediate: isIntermediateState };
          };

          const { checked, isIntermediate } = getCheckboxInitialState();

          state.treeTasksListCheckMap.set(taskId, {
            checked,
            isIntermediate,
            isCheckable,
            parentsIds: directParentId,
            childrenIds,
            taskStatus,
            isOrder,
          });
        });
      },
      addNewParent: (taskId, parentId) => {
        set((state) => {
          const selectedTask = state.treeTasksListCheckMap.get(taskId);
          if (selectedTask) {
            const updatedParentsIds = [...(selectedTask.parentsIds || []), parentId];
            state.treeTasksListCheckMap.set(taskId, {
              ...selectedTask,
              parentsIds: updatedParentsIds,
            });
          }
        });
      },
      onCheck: (taskId) => {
        set((state) => {
          const toggleChecked = (taskId: string, checkState?: boolean) => {
            const task = state.treeTasksListCheckMap.get(taskId);
            if (task && task.isCheckable) {
              const checked = typeof checkState !== 'undefined' ? checkState : !task.checked;
              const isOrder = task.isOrder;
              const isNotPlanned = task.taskStatus === TaskStatus.NotPlanned;
              const isIntermediate = typeof checkState !== 'undefined' ? checkState : !task.isIntermediate;

              state.treeTasksListCheckMap.set(taskId, {
                ...task,
                checked: isNotPlanned && !isOrder ? checked : false,
                isIntermediate: isNotPlanned && !isOrder ? false : isIntermediate,
              });
              if (task.childrenIds) {
                const childrenCheckStatus = isNotPlanned && !isOrder ? checked : isIntermediate;
                task.childrenIds.forEach((childId) => toggleChecked(childId, childrenCheckStatus));
              }
            }
          };

          const toggleParentCheck = (taskId: string) => {
            const task = state.treeTasksListCheckMap.get(taskId);
            if (task) {
              const isAnyChildSelectedOrIntermediate = task.childrenIds?.some((childId) => {
                const child = state.treeTasksListCheckMap.get(childId);
                return child && child.isCheckable ? child.checked || child.isIntermediate : false;
              });
              if (!task.checked) {
                if (isAnyChildSelectedOrIntermediate) {
                  state.treeTasksListCheckMap.set(taskId, {
                    ...task,
                    checked: false,
                    isIntermediate: true,
                  });
                } else {
                  state.treeTasksListCheckMap.set(taskId, {
                    ...task,
                    checked: false,
                    isIntermediate: false,
                  });
                }
              }
              if (task.parentsIds) {
                task.parentsIds.forEach((parentId) => toggleParentCheck(parentId));
              }
            }
          };

          toggleChecked(taskId);
          const parentsIds = state.treeTasksListCheckMap.get(taskId)?.parentsIds;
          if (parentsIds) {
            parentsIds.forEach((parentId) => toggleParentCheck(parentId));
          }
        });
      },
      onCheckAll: () => {
        set((state) => {
          state.treeTasksListCheckMap.clear();
        });
      },
    };
  }),
);
